import React, { useEffect, useRef, useState } from 'react';

import { Box, Flex, FlexProps, SimpleGrid, Space, TextInput, Textarea } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { CharacterCounter } from 'components/CharacterCounter';
import { EditAvatar } from 'components/EditAvatar';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { VoiceCard } from 'components/SmartAssistantSections/PhoneAgentBasicsSection/VoiceCard';
import { VoiceSelectorModal } from 'components/SmartAssistantSections/PhoneAgentBasicsSection/VoiceSelectorModal';
import { useSmartAssistantAvatarUrlHandling } from 'hooks/use-smart-assistant-avatar-url-handling';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { VoiceService } from 'services/voice-service';
import { VOICES_QUERY_KEY } from 'shared/constants/query-keys';
import { MAX_WIDTH } from 'shared/constants/theme';
import { PhoneAgentBasicsFormValues, SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { SMART_ASSISTANT_WELCOME_MESSAGE_MAX_LENGTH } from 'shared/utils/form-validators';

const MAX_INPUT_WIDTH = 445;

interface PhoneAgentBasicsSectionProps extends FlexProps {
  form: UseFormReturnType<PhoneAgentBasicsFormValues>;
  smartAssistant: SmartAssistant;
}

export function PhoneAgentBasicsSection({ form, smartAssistant, ...flexProps }: PhoneAgentBasicsSectionProps) {
  const { t } = useTranslation();
  const [isVoiceSelectorModalOpen, setIsVoiceSelectorModalOpen] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const { backToDefaultAvatar, handleImageDrop, isUpdatingAvatar } = useSmartAssistantAvatarUrlHandling(smartAssistant);

  // Unique audio ref used across all VoiceCards to play voice samples
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const allVoicesQuery = useQuery({
    queryKey: [VOICES_QUERY_KEY],
    queryFn: VoiceService.getVoices,
    onError: () => NotificationService.showResult(false, 'FEED_BACK.VOICE.FAILURE.GET_VOICES')
  });

  const handleVoiceSettings = () => {
    setIsVoiceSelectorModalOpen(true);
    resetAudio();
  };

  const resetAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingVoiceId(null);
  };

  // Reset audio when unmounting
  useEffect(() => {
    return () => {
      resetAudio();
    };
  }, []);

  return (
    <>
      <Flex w="100%" direction="row" align="flex-start" justify="space-between" maw={MAX_WIDTH} {...flexProps}>
        <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'md', cols: 1 }]} w="100%">
          <Flex direction="column" gap="md">
            <Flex w="100%" direction="row" align="flex-start">
              <EditAvatar
                src={smartAssistant.avatarUrl}
                size={50}
                radius={25}
                onRemove={backToDefaultAvatar}
                onFileDrop={handleImageDrop}
                loading={isUpdatingAvatar}
                mt={6}
              />
              <Space w="sm" />
              <TextInput
                w="100%"
                label={t('SMART_ASSISTANT.NAME.LABEL')}
                placeholder={t('SMART_ASSISTANT.NAME.PLACEHOLDER')}
                {...form.getInputProps('name')}
              />
            </Flex>

            <Flex direction="column" w="100%">
              <Textarea
                minRows={4}
                label={
                  <InputLabelWithInfo
                    label={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.GREETING_MESSAGE.TITLE')}
                    infoContentTranslationKey="SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.GREETING_MESSAGE.INFO"
                  />
                }
                maxRows={4}
                placeholder={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.GREETING_MESSAGE.PLACEHOLDER')}
                maxLength={SMART_ASSISTANT_WELCOME_MESSAGE_MAX_LENGTH}
                {...form.getInputProps('welcomeMessage')}
              />
              <CharacterCounter
                length={form.values.welcomeMessage.length}
                maxLength={SMART_ASSISTANT_WELCOME_MESSAGE_MAX_LENGTH}
              />
            </Flex>
          </Flex>

          <Flex direction="column" pos="relative" style={{ flexGrow: 1, paddingBottom: '15px' }}>
            <InputLabelWithInfo
              label={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.VOICE.TITLE')}
              infoContentTranslationKey={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.VOICE.INFO')}
            />
            <Box w={MAX_INPUT_WIDTH} pos="relative" top={0} left={0} style={{ flexGrow: 1, paddingBottom: '4px' }}>
              <VoiceCard
                audioRef={audioRef}
                isPlaying={playingVoiceId === form.values.voiceConfiguration.voiceId}
                voiceConfiguration={form.values.voiceConfiguration}
                onSettingsClick={handleVoiceSettings}
                maxWidth={MAX_INPUT_WIDTH}
                setPlayingVoiceId={setPlayingVoiceId}
                description={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.VOICE.DESCRIPTION')}
              />
            </Box>
          </Flex>
        </SimpleGrid>
      </Flex>

      <VoiceSelectorModal
        playingVoiceId={playingVoiceId}
        setPlayingVoiceId={setPlayingVoiceId}
        audioRef={audioRef}
        opened={isVoiceSelectorModalOpen}
        onClose={() => {
          setIsVoiceSelectorModalOpen(false);
          resetAudio();
        }}
        phoneAgentBasicsForm={form}
        availableVoices={allVoicesQuery.data ?? []}
      />
    </>
  );
}
